import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../_redux/user-assignment/UserAssignmentAction";
import {
  copy,
  DeleteButton,
  ExportButton,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  removalAlert,
  resultAlert
} from "../../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../../_metronic/_partials/controls";
import { PersonnelUsersFilter } from "./personnel-users-filter/PersonnelUsersFilter";
import { PersonnelUsersTable } from "./personnel-users-table/PersonnelUsersTable";
import { usePersonnelUsersUIContext } from "./PersonnelUsersUIContext";

export function PersonnelUsersCard() {
  // Personnel Users Card UI Context
  const personnelUsersUIContext = usePersonnelUsersUIContext();
  const personnelUsersUIProps = useMemo(() => {
    return {
      ids: personnelUsersUIContext.ids,
      setIds: personnelUsersUIContext.setIds,
      queryParams: personnelUsersUIContext.queryParams
    };
  }, [personnelUsersUIContext]);

  // Getting curret state of UserAssignment from store (Redux)
  const { currentState, currentActiveTerm } = useSelector(
    (state) => ({
      currentState: state.userAssignment,
      currentActiveTerm: state.auth.user.currentActiveTerm
    }),
    shallowEqual
  );  
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Personnel Users Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Personel Kullanıcı ve Kullanıcı Grubu Listesini Excele Aktarır */
  const exportPersonnelUsersToExcel = () => {
    dispatch(actions.getAllPersonnelUserAndUserGroups(personnelUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 30, 30, 30, 30];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Personel Türü", "Kullanıcı Grubu"];
        
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.personnelType,
        entity.userGroup
      ]));
        
      const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Personel Kullanıcı ve Kullanıcı Grubu Listesi";
      const sheetname = "Personel Kullanıcı Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Personel Kullanıcı ve Kullanıcı Grubu Listesini PDF e Aktarır */
  const exportPersonnelUsersToPDF = () => {
    dispatch(actions.getAllPersonnelUserAndUserGroups(personnelUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "TC Kimlik No",
          rows: entities.map(entity => entity.tcKimlikNo)
        },
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Soyadı",
          rows: entities.map(entity => entity.surname)
        },
        {
          header: "Personel Türü",
          rows: entities.map(entity => entity.personnelType)
        },
        {
          header: "Kullanıcı Grubu",
          rows: entities.map(entity => entity.userGroup)
        }
      ];
    
      const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Personel Kullanıcı ve Kullanıcı Grubu Listesi";
      exportToPDF(views, filename);
    });
  }
  
/** Belli Bir Personel Kullanıcı ve Kullanıcı Grubu Listesini Yazdırma Penceresini Açar */
  const printPersonnelUsers = () => {
    dispatch(actions.getAllPersonnelUserAndUserGroups(personnelUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Personel Kullanıcı ve Kullanıcı Grubu Listesi";
      const properties = ["TC Kimlik No", "Adı", "Soyadı", "Personel Türü", "Kullanıcı Grubu"];
    
      entities.forEach(entity => {
        data.push({
          "TC Kimlik No": entity.tcKimlikNo,
          "Adı": entity.name,
          "Soyadı": entity.surname,
          "Personel Türü": entity.personnelType,
          "Kullanıcı Grubu": entity.userGroup
        });
      });
    
      print({ data, properties, header });
    });    
  }
  
  /** Belli Bir Personel Kullanıcı ve Kullanıcı Grubu Listesini Panoya Kopyalar */
  const copyPersonnelUsers = () => {
    dispatch(actions.getAllPersonnelUserAndUserGroups(personnelUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Personel Türü", "Kullanıcı Grubu"];
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Personel Kullanıcı ve Kullanıcı Grubu Listesi";
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.personnelType,
        entity.userGroup
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Kullanıcı Listesini İlgili Kullanıcı Gruplarından Çıkarır */
  const removeUsers = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
    
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }

    const message = "İlgili kullanıcı(lar) kullanıcı grubu(ları)ndan çıkarılacaktır."
    removalAlert(message).then(result => {
      if (result.isConfirmed) dispatch(actions.removeUsers(ids)).then(result => onResult(result));
    });
  }
  
  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
    
    if (isSuccess) {
      setTimeout(() => {
        personnelUsersUIProps.setIds([]);
        dispatch(actions.getPersonnelUserAndUserGroups(personnelUsersUIProps.queryParams));
      }, 2500);
    }
  };

  const title = <>Personel Kullanıcı Listesi{personnelUsersUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{personnelUsersUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportPersonnelUsersToExcel}
                exportToPDF={exportPersonnelUsersToPDF}
                print={printPersonnelUsers}
                copy={copyPersonnelUsers}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton
                path="Navigation/Close"
                title="Seçilenleri Çıkar"
                method={() => removeUsers(personnelUsersUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <PersonnelUsersFilter />
        <PersonnelUsersTable
          removeUsers={removeUsers}
        />
      </CardBody>
    </Card>
  );
}